// Chunk column: 16×16 footprint × N sections spanning the dimension's `YRange`
// (ENG-6; default −64..319 / 24 sections, TAL-1). Empty sections are null (E-2).
// Block access is by LOCAL x/z (0..15) and world Y.
import { WORLD } from '../config';
import { Section, sectionIndexForY, localIndex, STATE_AIR, DEFAULT_RANGE, type YRange } from './section';
import {
  createHeightmaps,
  columnIndex,
  updateHeightOnSet,
  type Heightmaps,
} from './heightmap';

export type ChunkStatus = 'empty' | 'noise' | 'carved' | 'features' | 'light' | 'full';

/** A pending scheduled block-tick stored on its owning chunk (§3.2). Keeping the
 *  bucket per-chunk means pending ticks are saved and restored with the chunk
 *  (survive unload/save) instead of living in a world-global map that is dropped
 *  when the world is serialized. `due` is the absolute world tick it fires on. */
export interface PendingTick {
  x: number;
  y: number;
  z: number;
  due: number;
}

export function inYRange(y: number, range: YRange = DEFAULT_RANGE): boolean {
  return y >= range.minY && y <= range.maxY;
}

export class Chunk {
  /** Section slots, one per 16-block band of `range` (null for all-air, E-2). */
  readonly sections: (Section | null)[];
  readonly heightmaps: Heightmaps;
  status: ChunkStatus = 'empty';
  /** Scheduled block-ticks pending in this chunk (§3.2), saved with the chunk. */
  scheduledTicks: PendingTick[] = [];

  constructor(readonly cx: number, readonly cz: number, readonly range: YRange = DEFAULT_RANGE) {
    this.sections = new Array(range.sectionsPerColumn).fill(null);
    this.heightmaps = createHeightmaps(range.minY);
  }

  /** Global state id at local x/z (0..15) and world Y. Out-of-range Y is air. */
  getBlockState(lx: number, y: number, lz: number): number {
    if (!inYRange(y, this.range)) return STATE_AIR;
    const section = this.sections[sectionIndexForY(y, this.range)];
    return section ? section.getIndex(localIndex(lx, y, lz)) : STATE_AIR;
  }

  /**
   * Set a block by local x/z and world Y, allocating/freeing sections and
   * updating heightmaps. `isBlocking(state)` decides heightmap occupancy.
   */
  setBlockState(lx: number, y: number, lz: number, state: number, isBlocking: (s: number) => boolean): void {
    if (!inYRange(y, this.range)) return;
    const si = sectionIndexForY(y, this.range);
    let section = this.sections[si];
    if (!section) {
      if (state === STATE_AIR) return;
      section = new Section();
      this.sections[si] = section;
    }
    section.setIndex(localIndex(lx, y, lz), state);
    if (section.nonAirCount === 0) this.sections[si] = null;

    const col = columnIndex(lx, lz);
    const blocking = state !== STATE_AIR && isBlocking(state);
    const floorY = this.range.minY;
    // WORLD_SURFACE counts any non-air; MOTION_BLOCKING only blocking cells.
    // `range.minY` doubles as the NO_SURFACE sentinel for this dimension (TAL-5).
    // Null (all-air) sections are hopped 16 rows at a time so a downward scan
    // through a tall air gap stays O(sections) not O(blocks) (TAL-3).
    const surfaceScan = (fromY: number): number => {
      for (let yy = fromY; yy >= floorY; ) {
        if (!this.sections[sectionIndexForY(yy, this.range)]) { yy = (yy >> 4) * WORLD.SECTION - 1; continue; }
        if (this.getBlockState(lx, yy, lz) !== STATE_AIR) return yy + 1;
        yy--;
      }
      return floorY;
    };
    const blockingScan = (fromY: number): number => {
      for (let yy = fromY; yy >= floorY; ) {
        if (!this.sections[sectionIndexForY(yy, this.range)]) { yy = (yy >> 4) * WORLD.SECTION - 1; continue; }
        const s = this.getBlockState(lx, yy, lz);
        if (s !== STATE_AIR && isBlocking(s)) return yy + 1;
        yy--;
      }
      return floorY;
    };
    updateHeightOnSet(this.heightmaps.worldSurface, col, y, state !== STATE_AIR, surfaceScan);
    updateHeightOnSet(this.heightmaps.motionBlocking, col, y, blocking, blockingScan);
  }

  /** Highest non-air Y + 1 for a local column, or MIN_Y if empty. */
  surfaceY(lx: number, lz: number): number {
    return this.heightmaps.worldSurface[columnIndex(lx, lz)]!;
  }

  /** Rebuild both heightmaps from scratch — used after overlaying saved sections
   *  (ENG-10 load), where the incremental path doesn't apply. */
  recomputeHeightmaps(isBlocking: (s: number) => boolean): void {
    const ws = this.heightmaps.worldSurface;
    const mb = this.heightmaps.motionBlocking;
    ws.fill(this.range.minY);
    mb.fill(this.range.minY);
    let topSection = -1;
    for (let si = this.sections.length - 1; si >= 0; si--) {
      if (this.sections[si]) { topSection = si; break; }
    }
    if (topSection < 0) return; // all air
    const startY = (topSection - this.range.sectionYOffset) * WORLD.SECTION + WORLD.SECTION - 1;
    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        const col = columnIndex(lx, lz);
        let foundS = false, foundB = false;
        for (let y = startY; y >= this.range.minY && !(foundS && foundB); ) {
          // Hop null (all-air) sections 16 rows at a time (TAL-3).
          if (!this.sections[sectionIndexForY(y, this.range)]) { y = (y >> 4) * WORLD.SECTION - 1; continue; }
          const s = this.getBlockState(lx, y, lz);
          if (s !== STATE_AIR) {
            if (!foundS) { ws[col] = y + 1; foundS = true; }
            if (!foundB && isBlocking(s)) { mb[col] = y + 1; foundB = true; }
          }
          y--;
        }
      }
    }
  }
}
